import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DwComponent, DwComponentType, DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, map, startWith, switchMap, take, tap, timeout } from 'rxjs/operators';
import { AppMetaData, ChallengeDefinitionEntity, UserChallengeEntity, UserProfileEntity } from '../../../../meta-data/app-meta-data.service';
import { FitnessReferenceDataService, challengeRegistrationPrice } from '../../services/fitness-reference-data.service';
import { registrationGoals, visibleMilestones } from '../../../streaker-central/models/central.models';
import { homeTiming, inWindow } from './home-state';
import { RecipientRepositoryService, RecipientStory } from '../../../recipients/services/recipient-repository.service';

@DwComponent({ key: 'fitness-home', name: 'Holiday Streakers Home', componentType: DwComponentType.pageComponent, isGlobal: true })
@Component({ selector: 'app-home', templateUrl: './home.component.html', styleUrls: ['./home.component.scss'] })
export class HomeComponent implements OnInit, OnDestroy {
  currentChallenge: ChallengeDefinitionEntity;
  seasonMediaId: number;
  registrationPrice: number = null;
  registrationCount: number = null;
  membership: 'unknown' | 'unregistered' | 'pending' | 'registered' = 'unknown';
  isLoading = true;
  membershipLoading = true;
  registrationCountLoading = true;
  loadError = '';
  menuOpen = false;
  now = Date.now();
  recipients: RecipientStory[] = [];
  recipientsLoading = true;
  recipientsError = '';
  contactEmail = 'holidaystreakers@gmail.com';
  private refresh = new Subject<void>();
  private recipientChallenge = new Subject<number>();
  private subscriptions = new Subscription();

  constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
    private reference: FitnessReferenceDataService, public security: DwSecurityUserService,
    private router: Router, private route: ActivatedRoute, private recipientRepository: RecipientRepositoryService) {}

  ngOnInit(): void {
    this.subscriptions.add(combineLatest([this.recipientChallenge, this.recipientRepository.changes.pipe(startWith(null))]).pipe(switchMap(([challengeId]) => {
      this.recipientsLoading = true; this.recipientsError = ''; this.recipients = [];
      return this.recipientRepository.loadRecipients(challengeId).pipe(timeout(20000), catchError(() => {
        this.recipientsError = 'Unable to load recipients. Please try again.'; return of([]);
      }));
    })).subscribe(rows => { this.recipients = rows; this.recipientsLoading = false; }));
    this.subscriptions.add(combineLatest([
      this.security.securityContextChanged().pipe(startWith(null)), this.refresh.pipe(startWith(null)),
    ]).pipe(switchMap(() => {
      this.isLoading = true; this.loadError = ''; this.currentChallenge = null;
      this.seasonMediaId = null;
      this.registrationPrice = null; this.registrationCount = null; this.membership = 'unknown';
      this.membershipLoading = true; this.registrationCountLoading = true;
      return this.reference.getActiveChallenge().pipe(timeout(20000), take(1), switchMap(challenge => {
        this.currentChallenge = challenge;
        this.recipientChallenge.next(challenge.ChallengeDefinitionId);
        return combineLatest([
          of(challengeRegistrationPrice(challenge)),
          this.reference.getCompletedRegistrationCount(challenge.ChallengeDefinitionId).pipe(timeout(20000), take(1), catchError(() => of(null)), tap(() => this.registrationCountLoading = false), startWith(null)),
          this.loadMembership(challenge.ChallengeDefinitionId).pipe(timeout(20000), take(1), catchError(() => of('unknown' as const)), tap(() => this.membershipLoading = false), startWith('unknown' as const)),
          combineLatest([this.reference.getProductPreview(challenge.ProductId),
            this.reference.getChallengeMedia(challenge.ChallengeDefinitionId)]).pipe(
                map(([product, media]) => ({ mediaId: product?.mediaId || media.DEFAULT_USER_BANNER })),
                timeout(20000), take(1), catchError(() => of(null)), startWith(null)),
        ]);
      }), catchError(() => {
        this.loadError = 'Unable to load this season. Please try again.';
        return of(null);
      }));
    })).subscribe(result => {
      if (result) {
        [this.registrationPrice, this.registrationCount, this.membership] = result;
        this.seasonMediaId = result[3]?.mediaId;
      }
      this.isLoading = false;
    }));
    this.subscriptions.add(timer(0, 1000).subscribe(() => this.now = Date.now()));
  }
  ngOnDestroy(): void { this.subscriptions.unsubscribe(); this.refresh.complete(); this.recipientChallenge.complete(); }
  retryRecipients(): void { this.recipientRepository.refresh(); }
  retry(): void { this.reference.refresh(); this.refresh.next(); }
  get authenticated(): boolean { return !!this.security.securityState.isAuthenticated; }
  get timing() { return homeTiming(this.currentChallenge, this.now); }
  get storeOpen(): boolean {
    const c = this.currentChallenge;
    return !!c && inWindow(c.StoreStartDate, c.StoreEndDate, this.now);
  }
  get registrationOpen(): boolean {
    const c = this.currentChallenge;
    return !!c && inWindow(c.RegistrationStartDate, c.RegistrationEndDate, this.now);
  }
  get canRegister(): boolean { return this.storeOpen && this.registrationOpen && this.registrationPrice !== null; }
  get primaryLabel(): string {
    if (this.membership === 'registered') { return 'Open Streaker Central'; }
    if (this.membership === 'pending') { return ''; }
    if (this.authenticated && this.membership === 'unknown') { return ''; }
    return this.canRegister ? 'Register' : '';
  }
  get primaryRoute(): string { return this.membership === 'registered' ? '/streaker-central' : '/challenge-registration'; }
  get goal() {
    return this.currentChallenge ? visibleMilestones(registrationGoals(this.currentChallenge.ChallengeDefinitionId, this.registrationCount))[0] : null;
  }
  get goalPercent(): number { return this.goal && this.registrationCount !== null ? Math.min(100, this.registrationCount / this.goal.TargetValue * 100) : 0; }
  get remainingToGoal(): number { return this.goal ? Math.max(0, this.goal.TargetValue - this.registrationCount) : 0; }
  get seasonMessage(): string {
    if (!this.currentChallenge) { return ''; }
    if (this.timing.phase === 'unknown') { return 'Season dates are not available yet.'; }
    if (this.timing.phase === 'complete') { return 'This challenge has finished. Thank you for sharing the season with us.'; }
    if (this.timing.phase === 'active') { return 'The challenge is underway. Make a little time for movement every day.'; }
    return this.membership === 'registered' ? 'You’re registered. Get ready for kickoff in Streaker Central.' : '';
  }
  get progressCount(): number { return this.goal && this.registrationCount !== null ? Math.min(this.goal.TargetValue, this.registrationCount) : 0; }
  navigate(path: string, intent?: 'store' | 'donate'): void {
    // CAP's route guard stores the complete destination through authentication.
    const queryParams = { ...this.route.snapshot.queryParams };
    // A homepage action starts a new journey, rather than restoring a stale order.
    delete queryParams.order;
    delete queryParams.intent;
    if (intent) { queryParams.intent = intent; }
    this.router.navigate([path], { queryParams });
  }
  private loadMembership(challengeId: number): Observable<'unknown' | 'unregistered' | 'pending' | 'registered'> {
    if (!this.authenticated) { return of('unregistered'); }
    const context = this.security.securityContext;
    const user = context && (context.ApplicationUser || context.AuthenticatedUser);
    if (!user || !user.UserId) { return of('unknown'); }
    const query = AppMetaData.UserProfile.CreateQueryBuilder().addFields(a => [a.UserProfileId])
      .addFilterAnd(f => f.Equal(a => a.UserId, user.UserId));
    return this.orm.executeQuery<UserProfileEntity>(query).pipe(take(1), switchMap(profiles => {
      if (profiles.length > 1) { throw new Error('Multiple profiles found.'); }
      if (!profiles.length) { return of('unregistered' as const); }
      const enrollment = AppMetaData.UserChallenge.CreateQueryBuilder().addFields(a => [a.UserChallengeId, a.IsRegistrationComplete])
        .addFilterAnd(f => f.Equal(a => a.UserProfileId, profiles[0].UserProfileId))
        .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId));
      return this.orm.executeQuery<UserChallengeEntity>(enrollment).pipe(take(1), map(rows => {
        if (rows.length > 1) { throw new Error('Multiple enrollments found.'); }
        return rows.length ? rows[0].IsRegistrationComplete === true ? 'registered' as const : 'pending' as const : 'unregistered' as const;
      }));
    }));
  }
}
